// Admin Users Types
/** Причина изменения баланса (POST /admin/users/{id}/money) — обязательна, уходит в журнал аудита */
export type MoneyAdjustReason = 'PURCHASE_REFUND' | 'BONUS' | 'COMPENSATION' | 'MANUAL_DONATION';
export const MONEY_ADJUST_REASONS: MoneyAdjustReason[] = ['PURCHASE_REFUND', 'BONUS', 'COMPENSATION', 'MANUAL_DONATION'];

export interface AddMoneyDto {
  amount: number;
  applyBonus?: boolean;
  reason: MoneyAdjustReason;
}

export interface AddRouletteTokensDto {
  amount: number;
}

export interface GiveItemDto {
  itemId: number;
  quantity: number;
  location: 'WAREHOUSE' | 'CHARACTER';
  accountLogin?: string;
  characterName?: string;
}

export interface RemoveItemDto {
  itemId: number;
  objectId: number;
  quantity: number;
}

export interface RemoveItemsDto {
  items: RemoveItemDto[];
}

export interface RemoveInventoryItemDto {
  objectId: number;
  quantity: number;
}

export interface RemoveInventoryItemsDto {
  items: RemoveInventoryItemDto[];
}

export interface UpdateUserRoleDto {
  role: 'UNVERIFIED_USER' | 'VERIFIED_USER' | 'INFLUENCER' | 'QA' | 'SUPPORT' | 'ADMIN' | 'OWNER';
}

export interface UserListDto {
  id: number;
  username: string;
  email: string;
  role: string;
  money: number;
  rouletteTokens: number;
  createdAt: string;
}

export interface BulkRewardItemDto {
  itemId: number;
  quantity: number;
}

export interface BulkRewardRequestDto {
  eventKey: string;
  items: BulkRewardItemDto[];
  emails: string[];
}

export interface BulkRewardResultDto {
  grantedCount: number;
  granted: string[];
  alreadyRewarded: string[];
  userNotFound: string[];
}

/** Итог массового сброса расы: `DELETE /admin/races`. */
export interface ResetRacesResultDto {
  /** У скольких пользователей сайта снята раса. */
  clearedUsers: number;
  /** У скольких игровых аккаунтов снята раса. */
  clearedAccounts: number;
}

export interface PaginatedUsersDto {
  content: UserListDto[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

export interface AdminAccountDto {
  login: string;
  email: string;
  characters: AdminCharacterDto[];
}

export interface AdminCharacterDto {
  charId: number;
  charName: string;
  accountName: string;
  level: number;
  race?: string;
  className?: string;
  clanName?: string;
}

export interface UserDetailDto {
  id: number;
  username: string;
  email: string;
  role: string;
  money: number;
  rouletteTokens: number;
  createdAt: string;
  updatedAt: string;
  accounts: AdminAccountDto[];
}

export type UsersSortOption = 'money' | 'money_desc' | 'tokens' | 'tokens_desc' | 'created_at' | 'created_at_desc';

export interface UsersFilters {
  email?: string;
  username?: string;
  accountLogin?: string;
  characterName?: string;
  role?: string;
  sort?: UsersSortOption;
  page: number;
  size: number;
}

/** Созданный персонаж (GET /admin/users/characters) */
export interface CreatedCharacterDto {
  charId: number;
  charName: string;
  accountName: string;
  /** null — у аккаунта нет email */
  email: string | null;
  /** Уже локализованное название расы («Человек», «Тёмный эльф»); null — раса неизвестна */
  race: string | null;
  /** День создания `YYYY-MM-DD` (локальная дата игрового сервера, без времени) */
  createDate: string;
}

export interface PaginatedCreatedCharacterDto {
  content: CreatedCharacterDto[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

export type CreatedCharactersSortOption = 'created_at_desc' | 'created_at';

export interface CreatedCharactersFilters {
  /** `YYYY-MM-DD`, обе границы включительно */
  dateFrom?: string;
  dateTo?: string;
  email?: string;
  accountName?: string;
  sort?: CreatedCharactersSortOption;
  page: number;
  size: number;
}

// Audit Types
// Синхронизировано с enum параметра eventType в GET /admin/audits (OpenAPI, 27.09.2026). Порядок — как в спеке.
export enum AuditEventType {
  LOGIN_SUCCESS = 'LOGIN_SUCCESS',
  LOGIN_FAILED = 'LOGIN_FAILED',
  LOGOUT = 'LOGOUT',
  OAUTH_LOGIN_GOOGLE = 'OAUTH_LOGIN_GOOGLE',
  OAUTH_LOGIN_YANDEX = 'OAUTH_LOGIN_YANDEX',
  OAUTH_LOGIN_VKID = 'OAUTH_LOGIN_VKID',
  ROLE_CHANGED = 'ROLE_CHANGED',
  MONEY_ADJUSTED = 'MONEY_ADJUSTED',
  TOKENS_ADJUSTED = 'TOKENS_ADJUSTED',
  ITEM_GIVEN = 'ITEM_GIVEN',
  WAREHOUSE_ITEM_REMOVED = 'WAREHOUSE_ITEM_REMOVED',
  INVENTORY_ITEM_REMOVED = 'INVENTORY_ITEM_REMOVED',
  PAYMENT_COMPLETED = 'PAYMENT_COMPLETED',
  ITEM_MOVED_TO_GAME = 'ITEM_MOVED_TO_GAME',
  ITEM_MOVED_TO_WAREHOUSE = 'ITEM_MOVED_TO_WAREHOUSE',
  SHOP_PURCHASE = 'SHOP_PURCHASE',
  CHARACTER_RENAME = 'CHARACTER_RENAME',
  ROULETTE_TOKENS_PURCHASE = 'ROULETTE_TOKENS_PURCHASE',
  PROMO_ACTIVATED = 'PROMO_ACTIVATED',
  L2_ACCOUNT_CREATED = 'L2_ACCOUNT_CREATED',
  L2_PASSWORD_CHANGED = 'L2_PASSWORD_CHANGED',
  USER_PASSWORD_CHANGED = 'USER_PASSWORD_CHANGED',
  EMAIL_VERIFIED = 'EMAIL_VERIFIED',
  TWO_FACTOR_ENABLED = 'TWO_FACTOR_ENABLED',
  TWO_FACTOR_DISABLED = 'TWO_FACTOR_DISABLED',
  ROULETTE_SPIN = 'ROULETTE_SPIN',
  SUPPORT_TICKET_STATUS_CHANGED = 'SUPPORT_TICKET_STATUS_CHANGED',
  REFERRAL_BOUND_BY_ADMIN = 'REFERRAL_BOUND_BY_ADMIN',
  MONEY_EXCHANGED = 'MONEY_EXCHANGED',
}

export interface AuditLogDto {
  id: number;
  createdAt: string;
  eventType: AuditEventType;
  userEmail: string;
  message: string;
  ipAddress: string;
  userAgent: string;
}

export interface PaginatedAuditLogDto {
  content: AuditLogDto[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}
